import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BnLoadingService, EntityPagedResult } from "@bndes/bndes-ux4/bndes-angular-utils";
import { BnUsuarioService } from "@bndes/bndes-ux4/bn-usuario";
import { ROLE_AGENTE_CONSULTA, ROLE_AGENTE_ENVIO, ROLE_GESTOR_CONSULTA } from 'src/app/constantes/permissao.constants';
import { AgenteFinanceiro } from 'src/app/model/agente-financeiro.model';
import { ProtocoloArquivo, STATUS_PROTOCOLO, StatusProtocolo } from 'src/app/model/protocolo-arquivo.model';
import { PermissaoService } from 'src/app/shared/permissao.service';
import { AgenteFinanceiroService } from 'src/app/shared/services/agente-financeiro.service';
import { LeilaoService } from '../leilao.service';
import { DatePipe } from '@angular/common';
import { MensagemService } from '../../mensagem.service';

const ID_TIPO_PROTOCOLO = 10; // tipo protocolo leilão
@Component({
  selector: 'app-leilao-pesquisa',
  templateUrl: './leilao-pesquisa.component.html',
  styleUrls: ['./leilao-pesquisa.component.scss'],
  standalone: false
})
export class LeilaoPesquisaComponent implements OnInit {

  pesquisaForm: UntypedFormGroup;
  cnpjUsuario: string;
  agenteFinanceiroLogado: AgenteFinanceiro;
  statusSelecionado: StatusProtocolo;
  podeEscolherAgente: boolean = false;
  podeEnviarArquivo: boolean = false;
  agentesFinanceiros: AgenteFinanceiro[] = [];
  valoresStatusProtocolo = STATUS_PROTOCOLO;
  protocolos: ProtocoloArquivo[];
  atributosPaginacao: EntityPagedResult<ProtocoloArquivo>;
  opcoesPaginacao: number[];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private permissaoService: PermissaoService,
    private loadingService: BnLoadingService,
    private agenteService: AgenteFinanceiroService,
    private bnUsuarioService: BnUsuarioService,
    private leilaoService: LeilaoService,
    private datePipe: DatePipe,
    private mensagemService: MensagemService,
  ) {

  }

  ngOnInit(): void {
    this.opcoesPaginacao = [10, 15, 20];
    this.atributosPaginacao = {
      page: 1,
      pageSize: this.opcoesPaginacao[0],
      pages: undefined,
      count: undefined,
      list: undefined
    };
    this.criaForm();
    this.carregaAgentesFinanceiros();

  }

  carregaAgentesFinanceiros() {
    this.loadingService.bloqueiaTela();
    this.agenteService.getAgentesFinanceiros().subscribe((response => {
      this.agentesFinanceiros = response ? response.sort((a, b) => a?.sigla ? a.sigla.localeCompare(b.sigla) : 0) : null;
      this.setPermissaoUsuarioLogado();
      this.loadingService.liberaTela();
    }));
  }

  setPermissaoUsuarioLogado() {
    this.permissaoService.getPermissoesUsuarioAutenticado().subscribe((resp: any) => {
      this.podeEscolherAgente = resp && resp[ROLE_GESTOR_CONSULTA];
      this.podeEnviarArquivo = resp && resp[ROLE_AGENTE_ENVIO];
      if (resp && resp[ROLE_AGENTE_ENVIO] || resp[ROLE_AGENTE_CONSULTA]) {
        this.bnUsuarioService.usuario$.subscribe(usuario => {
          if (usuario && usuario.cnpj) {
            this.cnpjUsuario = usuario.cnpj;
            this.setAgenteFinanceiroLogado();
          }
        });
      }
    });
  }

  setAgenteFinanceiroLogado() {
    this.agenteFinanceiroLogado = this.agentesFinanceiros.find(a => a.cnpj === this.cnpjUsuario);
  }

  criaForm() {
    this.pesquisaForm = this.formBuilder.group({
      agenteFinanceiro: [null],
      statusProtocolo: [''],
      modoEnvio: [''],
      dataEnvioInicial: [''],
      dataEnvioFinal: [''],
      idProtocoloNegocio: [''],
      idSolicitacaoApiExt: [''],
    });
  }

  buscarProtocolosLeiloesEnviados(paginado?: boolean) {
    let params = this.informacoesPreenchidas();
    params.pagina = paginado ? this.atributosPaginacao.page : 1;
    params.limite = this.atributosPaginacao.pageSize;

    this.loadingService.bloqueiaTela();
    this.leilaoService.getProtocolosLeiloesEnviados(params).subscribe((response) => {
      this.protocolos = response ? response.resultados : undefined;
      this.atributosPaginacao.count = response ? response.totalRegistrosSemPaginacao : 0;
      this.loadingService.liberaTela();
    }, () => {
      this.loadingService.liberaTela();
      this.mensagemService.adicionarMensagemErro('Erro ao buscar protocolos de leilões enviados.');
    })
  }

  informacoesPreenchidas() {
    let dadosPreenchidos: any = {};
    const form = this.pesquisaForm.value;

    if (this.agenteFinanceiroLogado && this.cnpjUsuario) {
      dadosPreenchidos.cnpjAgente = this.agenteFinanceiroLogado.cnpj;
    }

    if (this.podeEscolherAgente && form.agenteFinanceiro) {
      const idAF = form.agenteFinanceiro;
      const cnpjSelecionado = this.agentesFinanceiros.find((af) => af.id === idAF)?.cnpj;
      dadosPreenchidos.cnpjAgente = cnpjSelecionado;
    }

    let statusProtocolo;
    if (form.statusProtocolo) {
      const labelStatus = form.statusProtocolo;
      statusProtocolo = STATUS_PROTOCOLO.find(status => status.label === labelStatus);
    }

    if (statusProtocolo) {
      dadosPreenchidos.statusProtocolo = statusProtocolo.codigo;
    }

    let apenasValidacao;
    if (form.modoEnvio) {
      const modoEnvio = form.modoEnvio;
      apenasValidacao = (modoEnvio === 'Todos' || modoEnvio === '') ? undefined : (modoEnvio === 'Apenas validação');
    }

    if (apenasValidacao !== undefined) {
      dadosPreenchidos.apenasValidacao = apenasValidacao;
    }

    if (form.dataEnvioInicial) {
      dadosPreenchidos.dataEnvioInicial = this.datePipe.transform(form.dataEnvioInicial, 'yyyyMMdd');
    }

    if (form.dataEnvioFinal) {
      dadosPreenchidos.dataEnvioFinal = this.datePipe.transform(form.dataEnvioFinal, 'yyyyMMdd');
    }

    if (form.idProtocoloNegocio) {
      dadosPreenchidos.idProtocoloNegocio = form.idProtocoloNegocio;
    }

    if (form.idSolicitacaoApiExt) {
      dadosPreenchidos.idSolicitacaoApiExt = form.idSolicitacaoApiExt;
    }

    dadosPreenchidos.tiposProtocolos = ID_TIPO_PROTOCOLO;

    return dadosPreenchidos;
  }

  mudancaPagina(novaPagina: EntityPagedResult<ProtocoloArquivo>) {
    this.atributosPaginacao = novaPagina;
    this.buscarProtocolosLeiloesEnviados(true);
  }

  limpar() {
    if (this.agenteFinanceiroLogado && this.cnpjUsuario) {
      this.pesquisaForm.reset({
        agenteFinanceiro: this.agenteFinanceiroLogado,
        statusProtocolo: "Todos",
        modoEnvio: "Todos",
      });
    } else {
      this.pesquisaForm.reset({
        statusProtocolo: "Todos",
        modoEnvio: "Todos",
      });
    }
    this.protocolos = undefined;
  }

}
